/**
 * Multi-product quote search & add (NIJ-68).
 * Loaded on the Request a Quote page. Provides autocomplete product search
 * with thumbnails, variant selection, and add/remove functionality.
 *
 * Injects UI inside the Shopify Forms shadow DOM, directly after the
 * product title field. Products are submitted via the existing form fields.
 */
(function () {
  'use strict';

  var SEARCH_LIMIT = 6;
  var DEBOUNCE_MS = 300;
  var ROOT_URL = (window.theme && window.theme.routes && window.theme.routes.root_url) || '';
  var ORIGIN = window.location.origin;

  // ── State ──
  var products = []; // [{ variantId, productTitle, variantTitle, imageUrl }]
  var container = null;
  var searchInput = null;
  var resultsDropdown = null;
  var variantPickerWrap = null;
  var productListEl = null;
  var addLink = null;
  var debounceTimer = null;
  var variantField = null;  // original hidden variant_id input
  var titleField = null;    // original product_title input
  var titleFieldContainer = null;
  var pendingRequiredVariant = false; // true when a required variant picker is open

  // ── Shadow DOM traversal ──
  function walkDOM(node, callback) {
    if (!node) return;
    if (node.nodeType === 1) {
      callback(node);
      if (node.shadowRoot) walkDOM(node.shadowRoot, callback);
    }
    if (node.nodeType === 1 || node.nodeType === 11) {
      var children = node.childNodes;
      for (var i = 0; i < children.length; i++) {
        walkDOM(children[i], callback);
      }
    }
  }

  function matchesField(input, key) {
    if (!key) return false;
    var k = key.toLowerCase();
    var id = (input.id || '').toLowerCase();
    var name = (input.getAttribute('name') || input.name || '').toLowerCase();
    var testId = (input.getAttribute('data-testid') || '').toLowerCase();
    return id.indexOf(k) !== -1 || name.indexOf(k) !== -1 || testId.indexOf(k) !== -1;
  }

  function findFormFields() {
    var variant = null, title = null;
    walkDOM(document.body, function (el) {
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        if (matchesField(el, 'variant_id')) variant = el;
        if (matchesField(el, 'product_title')) title = el;
      }
    });
    return { variant: variant, title: title };
  }

  // ── Init ──
  function init() {
    var fields = findFormFields();
    if (!fields.title) return false;

    titleField = fields.title;
    variantField = fields.variant;

    // Find the field container (the wrapping div in the form)
    titleFieldContainer = titleField.closest('[class*="formFieldContainer"], [class*="FieldContainer"], [class*="_formFieldContainer"]') || titleField.parentElement;
    if (!titleFieldContainer) return false;

    // Read initial product from URL params
    var params = new URLSearchParams(window.location.search);
    var initialVariantId = params.get('variant_id');
    var initialProductTitle = params.get('product_title');

    // Inject CSS into the shadow root
    injectStyles(titleField);

    // Build UI container and insert after the title field container
    container = document.createElement('div');
    container.className = 'quote-product-search';
    container.innerHTML = buildContainerHTML();
    titleFieldContainer.parentNode.insertBefore(container, titleFieldContainer.nextSibling);

    // Hide the original product_title field container
    titleFieldContainer.style.display = 'none';
    // Hide the variant_id field container
    if (variantField) {
      var variantContainer = variantField.closest('[class*="formFieldContainer"], [class*="FieldContainer"], [class*="_formFieldContainer"]') || variantField.parentElement;
      if (variantContainer) variantContainer.style.display = 'none';
    }

    // Cache elements
    productListEl = container.querySelector('.quote-product-list');
    addLink = container.querySelector('.quote-add-product-link');
    searchInput = container.querySelector('.quote-search-input');
    resultsDropdown = container.querySelector('.quote-search-results');
    variantPickerWrap = container.querySelector('.quote-variant-picker-wrap');

    // Add initial product if present
    if (initialVariantId && initialProductTitle) {
      addProduct({
        variantId: initialVariantId,
        productId: '',
        productTitle: decodeURIComponent(initialProductTitle),
        variantTitle: '',
        imageUrl: ''
      });
      fetchProductByVariantId(initialVariantId, decodeURIComponent(initialProductTitle));
    }

    // Bind events
    addLink.addEventListener('click', function (e) {
      e.preventDefault();
      showSearch();
    });

    searchInput.addEventListener('input', function () {
      var q = searchInput.value.trim();
      if (q.length < 2) {
        hideResults();
        return;
      }
      debounceSearch(q);
    });

    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hideSearch();
    });

    // Close dropdown on click outside — listen on the shadow root
    // (events from shadow DOM don't bubble to document with original target)
    var shadowRoot = container.getRootNode();
    if (shadowRoot && shadowRoot !== document) {
      shadowRoot.addEventListener('click', function (e) {
        if (container && !container.contains(e.target)) {
          hideResults();
          // Don't hide variant picker on outside click — let cancel button handle it
        }
      });
    }

    // Intercept form submission when a required variant picker is unresolved.
    // Shopify Forms uses JS-based submission, so we intercept both the form
    // submit event and any submit/button clicks inside the shadow root.
    var shadowRoot = titleField.getRootNode();
    var form = titleField.closest('form');

    function blockSubmitIfNeeded(e) {
      if (!pendingRequiredVariant) return;
      var select = variantPickerWrap.querySelector('.quote-variant-select');
      // Allow submit if a variant has been selected in the dropdown
      if (select && select.value) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var msg = variantPickerWrap.querySelector('.quote-variant-required-msg');
      if (select) select.classList.add('quote-variant-select--invalid');
      if (msg) msg.style.display = 'block';
    }

    if (form) {
      form.addEventListener('submit', blockSubmitIfNeeded, true);
    }

    // Also intercept click on the submit button directly (Shopify Forms
    // may bypass the native submit event)
    if (shadowRoot && shadowRoot !== document) {
      shadowRoot.addEventListener('click', function (e) {
        if (!pendingRequiredVariant) return;
        var btn = e.target.closest('button[type="submit"], input[type="submit"], [data-testid*="submit"]');
        if (!btn) {
          // Also catch buttons with submit-like text
          if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
            var b = e.target.closest('button') || e.target;
            var text = (b.textContent || '').trim().toLowerCase();
            if (text === 'submit' || text === 'send') {
              btn = b;
            }
          }
        }
        if (btn && !container.contains(btn)) {
          blockSubmitIfNeeded(e);
        }
      }, true);
    }

    updateUI();
    return true;
  }

  function injectStyles(refElement) {
    // Find the shadow root that contains the form field
    var root = refElement.getRootNode();
    if (root && root !== document) {
      // We're inside a shadow DOM — inject styles there
      var style = document.createElement('style');
      style.textContent = getInlineCSS();
      root.appendChild(style);
    }
  }

  function getInlineCSS() {
    return '' +
      '.quote-product-search { margin: 8px 0 16px; }' +
      '.quote-product-list { display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px; }' +
      '.quote-product-chip { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; }' +
      '.quote-product-chip-img { width: 32px; height: 32px; object-fit: cover; border-radius: 3px; flex-shrink: 0; }' +
      '.quote-product-chip-title { flex: 1; color: #333; line-height: 1.3; }' +
      '.quote-product-variant { color: #777; font-size: 13px; }' +
      '.quote-product-chip-remove { flex-shrink: 0; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; border: none; background: transparent; font-size: 16px; color: #999; cursor: pointer; border-radius: 50%; padding: 0; }' +
      '.quote-product-chip-remove:hover { background: #e0e0e0; color: #333; }' +
      '.quote-add-product-link { display: inline-block; margin-bottom: 12px; font-size: 14px; color: #1878b9; cursor: pointer; text-decoration: underline; border: none; background: none; padding: 0; }' +
      '.quote-add-product-link:hover { color: #0f5f8a; }' +
      '.quote-search-wrap { position: relative; margin-bottom: 12px; }' +
      '.quote-search-input { width: 100%; padding: 9px 10px; font-size: 14px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }' +
      '.quote-search-input:focus { outline: none; border-color: #555; }' +
      '.quote-search-results { position: absolute; top: 100%; left: 0; right: 0; background: #fff; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 100; max-height: 300px; overflow-y: auto; }' +
      '.quote-search-result { display: flex; align-items: center; gap: 8px; width: 100%; padding: 7px 10px; border: none; background: transparent; text-align: left; cursor: pointer; font-size: 14px; border-bottom: 1px solid #f0f0f0; }' +
      '.quote-search-result:last-child { border-bottom: none; }' +
      '.quote-search-result:hover { background: #f8f8f8; }' +
      '.quote-search-result-img { width: 36px; height: 36px; object-fit: cover; border-radius: 3px; flex-shrink: 0; }' +
      '.quote-search-result-img--placeholder { display: block; background: #eee; width: 36px; height: 36px; border-radius: 3px; }' +
      '.quote-search-no-results { padding: 10px; color: #999; font-size: 14px; text-align: center; }' +
      '.quote-variant-picker { padding: 10px; background: #fafafa; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px; }' +
      '.quote-variant-label { display: block; margin-bottom: 6px; font-size: 14px; }' +
      '.quote-variant-select { width: 100%; padding: 8px; font-size: 14px; border: 1px solid #ccc; border-radius: 4px; margin-bottom: 8px; box-sizing: border-box; }' +
      '.quote-variant-btns { display: flex; gap: 8px; align-items: center; }' +
      '.quote-variant-add-btn { padding: 6px 14px; font-size: 13px; background: #202020; color: #fff; border: none; border-radius: 4px; cursor: pointer; }' +
      '.quote-variant-cancel-btn { font-size: 13px; color: #999; border: none; background: none; cursor: pointer; text-decoration: underline; padding: 0; }' +
      '.quote-variant-loading, .quote-variant-error { padding: 10px; font-size: 14px; color: #777; }' +
      '.quote-variant-error { color: #c00; }' +
      '.quote-variant-select--invalid { border-color: #c00; }' +
      '.quote-variant-required-msg { color: #c00; font-size: 13px; margin: 0 0 6px; display: none; }';
  }

  function buildContainerHTML() {
    return '' +
      '<div class="quote-product-list"></div>' +
      '<button type="button" class="quote-add-product-link">+ Add another product to your quote</button>' +
      '<div class="quote-search-wrap" style="display:none;">' +
        '<input type="text" class="quote-search-input" placeholder="Search for a product..." autocomplete="off" />' +
        '<div class="quote-search-results" style="display:none;"></div>' +
      '</div>' +
      '<div class="quote-variant-picker-wrap" style="display:none;"></div>';
  }

  // ── Search ──
  function debounceSearch(q) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { performSearch(q); }, DEBOUNCE_MS);
  }

  function performSearch(q) {
    var url = ORIGIN + ROOT_URL + '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources%5Btype%5D=product&resources%5Blimit%5D=' + SEARCH_LIMIT;

    // Use XMLHttpRequest to avoid Shopify's fetch interceptors
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var data = JSON.parse(xhr.responseText);
          var items = (data.resources && data.resources.results && data.resources.results.products) || [];
          renderResults(items);
        } catch (e) {
          console.error('Quote search parse error:', e);
        }
      }
    };
    xhr.onerror = function () {
      console.error('Quote search network error');
    };
    xhr.send();
  }

  function renderResults(items) {
    if (!items.length) {
      resultsDropdown.innerHTML = '<div class="quote-search-no-results">No products found</div>';
      resultsDropdown.style.display = 'block';
      return;
    }

    var html = '';
    for (var i = 0; i < items.length; i++) {
      var p = items[i];
      var imgUrl = (p.featured_image && p.featured_image.url) || p.image || '';
      var thumbUrl = imgUrl ? imgUrl + '&width=80' : '';
      html += '<button type="button" class="quote-search-result" data-handle="' + escapeAttr(p.handle) + '" data-title="' + escapeAttr(p.title) + '" data-image="' + escapeAttr(thumbUrl) + '">' +
        (thumbUrl ? '<img class="quote-search-result-img" src="' + escapeAttr(thumbUrl) + '" alt="" width="36" height="36" />' : '<span class="quote-search-result-img--placeholder"></span>') +
        '<span class="quote-search-result-title">' + escapeHTML(p.title) + '</span>' +
      '</button>';
    }
    resultsDropdown.innerHTML = html;
    resultsDropdown.style.display = 'block';

    var buttons = resultsDropdown.querySelectorAll('.quote-search-result');
    for (var j = 0; j < buttons.length; j++) {
      buttons[j].addEventListener('click', onProductSelected);
    }
  }

  function onProductSelected(e) {
    e.stopPropagation(); // prevent outside-click handler from firing
    var btn = e.currentTarget;
    var handle = btn.dataset.handle;
    var title = btn.dataset.title;
    var imageUrl = btn.dataset.image;

    hideResults();
    searchInput.value = '';
    // Hide search input, show variant picker in its place
    container.querySelector('.quote-search-wrap').style.display = 'none';
    showVariantPicker(handle, title, imageUrl);
  }

  // ── Variant Picker ──
  function fetchQuoteMeta(handle, callback) {
    var url = ORIGIN + ROOT_URL + '/products/' + handle + '?view=1-quote-meta';
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onload = function () {
      if (xhr.status < 200 || (xhr.status >= 300 && xhr.status !== 304)) { callback(false); return; }
      try {
        var meta = JSON.parse(xhr.responseText);
        callback(!!meta.variant_required);
      } catch (e) { callback(false); }
    };
    xhr.onerror = function () { callback(false); };
    xhr.send();
  }

  function showVariantPicker(handle, productTitle, imageUrl) {
    variantPickerWrap.innerHTML = '<div class="quote-variant-loading">Loading options...</div>';
    variantPickerWrap.style.display = 'block';

    var url = ORIGIN + ROOT_URL + '/products/' + handle + '.json';
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onload = function () {
      if (xhr.status < 200 || xhr.status >= 300) {
        variantPickerWrap.innerHTML = '<div class="quote-variant-error">Could not load product. Please try again.</div>';
        return;
      }
      try {
        var data = JSON.parse(xhr.responseText);
        var p = data.product;
        var productId = String(p.id);
        var variants = p.variants || [];

        if (!imageUrl && p.images && p.images.length) {
          imageUrl = p.images[0].src + '&width=80';
        }

        if (variants.length === 1) {
          addProduct({
            variantId: String(variants[0].id),
            productId: productId,
            productTitle: productTitle,
            variantTitle: variants[0].title === 'Default Title' ? '' : variants[0].title,
            imageUrl: imageUrl
          });
          hideVariantPicker();
          hideSearch();
        } else {
          fetchQuoteMeta(handle, function (variantRequired) {
            pendingRequiredVariant = variantRequired;
            renderVariantPicker(variants, productTitle, imageUrl, productId, variantRequired);
          });
        }
      } catch (e) {
        variantPickerWrap.innerHTML = '<div class="quote-variant-error">Could not load product. Please try again.</div>';
      }
    };
    xhr.onerror = function () {
      variantPickerWrap.innerHTML = '<div class="quote-variant-error">Network error. Please try again.</div>';
    };
    xhr.send();
  }

  function renderVariantPicker(variants, productTitle, imageUrl, productId, variantRequired) {
    var html = '<div class="quote-variant-picker">' +
      '<label class="quote-variant-label">Select option for <strong>' + escapeHTML(productTitle) + '</strong>:</label>' +
      '<p class="quote-variant-required-msg">Please select an option</p>' +
      '<select class="quote-variant-select">' +
      '<option value="">Choose an option...</option>';
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      html += '<option value="' + v.id + '" data-title="' + escapeAttr(v.title) + '">' +
        escapeHTML(v.title) + '</option>';
    }
    html += '</select>' +
      '<div class="quote-variant-btns">' +
        '<button type="button" class="quote-variant-add-btn">Add to Quote</button>' +
        '<button type="button" class="quote-variant-cancel-btn">Cancel</button>' +
      '</div>' +
    '</div>';
    variantPickerWrap.innerHTML = html;

    var select = variantPickerWrap.querySelector('.quote-variant-select');
    var requiredMsg = variantPickerWrap.querySelector('.quote-variant-required-msg');

    select.addEventListener('change', function () {
      select.classList.remove('quote-variant-select--invalid');
      requiredMsg.style.display = 'none';
    });

    variantPickerWrap.querySelector('.quote-variant-add-btn').addEventListener('click', function () {
      var opt = select.options[select.selectedIndex];
      if (!opt || !opt.value) {
        if (variantRequired) {
          select.classList.add('quote-variant-select--invalid');
          requiredMsg.style.display = 'block';
          return;
        }
        addProduct({
          variantId: '',
          productId: productId,
          productTitle: productTitle,
          variantTitle: '',
          imageUrl: imageUrl
        });
        hideVariantPicker();
        hideSearch();
        return;
      }
      addProduct({
        variantId: opt.value,
        productId: productId,
        productTitle: productTitle,
        variantTitle: opt.dataset.title || opt.textContent,
        imageUrl: imageUrl
      });
      hideVariantPicker();
      hideSearch();
    });
    variantPickerWrap.querySelector('.quote-variant-cancel-btn').addEventListener('click', hideVariantPicker);
  }

  // ── Product List ──
  function addProduct(item) {
    for (var i = 0; i < products.length; i++) {
      var existing = products[i];
      if (item.variantId && existing.variantId === item.variantId) return;
      if (!item.variantId && !existing.variantId && existing.productId === item.productId) return;
    }
    products.push(item);
    syncFormFields();
    updateUI();
  }

  function removeProduct(index) {
    products.splice(index, 1);
    syncFormFields();
    updateUI();
  }

  function syncFormFields() {
    // Write all products into the original form fields as combined values
    // This ensures the data submits through the Shopify Forms app
    if (variantField) {
      variantField.value = products.map(function (p) {
        return p.variantId || ('product:' + p.productId);
      }).join(', ');
      variantField.dispatchEvent(new Event('input', { bubbles: true }));
      variantField.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (titleField) {
      titleField.value = products.map(function (p) {
        return p.productTitle + (p.variantTitle ? ' — ' + p.variantTitle : '');
      }).join(' | ');
      titleField.dispatchEvent(new Event('input', { bubbles: true }));
      titleField.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function updateUI() {
    renderProductList();
    if (products.length === 0) {
      addLink.textContent = '+ Add a product to your quote';
    } else {
      addLink.textContent = '+ Add another product to your quote';
    }
  }

  function renderProductList() {
    if (!products.length) {
      productListEl.innerHTML = '';
      return;
    }

    var showRemove = products.length > 1;
    var html = '';
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var displayTitle = escapeHTML(p.productTitle) +
        (p.variantTitle ? ' <span class="quote-product-variant">— ' + escapeHTML(p.variantTitle) + '</span>' : '');
      html += '<div class="quote-product-chip">' +
        (p.imageUrl ? '<img class="quote-product-chip-img" src="' + escapeAttr(p.imageUrl) + '" alt="" width="32" height="32" />' : '') +
        '<span class="quote-product-chip-title">' + displayTitle + '</span>' +
        (showRemove ? '<button type="button" class="quote-product-chip-remove" data-index="' + i + '" aria-label="Remove">&times;</button>' : '') +
      '</div>';
    }
    productListEl.innerHTML = html;

    var removeBtns = productListEl.querySelectorAll('.quote-product-chip-remove');
    for (var j = 0; j < removeBtns.length; j++) {
      removeBtns[j].addEventListener('click', function (e) {
        removeProduct(parseInt(e.currentTarget.dataset.index, 10));
      });
    }
  }

  // ── Helpers ──
  function showSearch() {
    container.querySelector('.quote-search-wrap').style.display = 'block';
    searchInput.focus();
    addLink.style.display = 'none';
  }

  function hideSearch() {
    container.querySelector('.quote-search-wrap').style.display = 'none';
    searchInput.value = '';
    hideResults();
    addLink.style.display = '';
  }

  function hideResults() {
    resultsDropdown.style.display = 'none';
    resultsDropdown.innerHTML = '';
  }

  function hideVariantPicker() {
    variantPickerWrap.style.display = 'none';
    variantPickerWrap.innerHTML = '';
    addLink.style.display = '';
    pendingRequiredVariant = false;
  }

  function fetchProductByVariantId(variantId, productTitle) {
    var url = ORIGIN + ROOT_URL + '/search/suggest.json?q=' + encodeURIComponent(productTitle) +
      '&resources%5Btype%5D=product&resources%5Blimit%5D=1';
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onload = function () {
      if (xhr.status < 200 || xhr.status >= 300) return;
      try {
        var data = JSON.parse(xhr.responseText);
        var items = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (items.length && items[0].featured_image) {
          products[0].imageUrl = items[0].featured_image.url + '&width=80';
          // Fetch variant title
          var xhr2 = new XMLHttpRequest();
          xhr2.open('GET', ORIGIN + ROOT_URL + '/products/' + items[0].handle + '.json', true);
          xhr2.onload = function () {
            if (xhr2.status < 200 || xhr2.status >= 300) return;
            try {
              var pData = JSON.parse(xhr2.responseText);
              products[0].productId = String(pData.product.id);
              var variants = pData.product.variants || [];
              for (var i = 0; i < variants.length; i++) {
                if (String(variants[i].id) === String(variantId)) {
                  products[0].variantTitle = variants[i].title === 'Default Title' ? '' : variants[i].title;
                  break;
                }
              }
              syncFormFields();
              updateUI();
            } catch (e) { /* silent */ }
          };
          xhr2.send();
        }
      } catch (e) { /* silent */ }
    };
    xhr.send();
  }

  function escapeHTML(s) {
    var div = document.createElement('div');
    div.textContent = s || '';
    return div.innerHTML;
  }

  function escapeAttr(s) {
    return (s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Boot ──
  function boot() {
    if (init()) return;
    var attempts = 0;
    var interval = setInterval(function () {
      attempts++;
      if (init() || attempts > 100) clearInterval(interval);
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
